import { useLoaderData, useLocation, useNavigate } from 'react-router';

import { Card, Icon, KpiCard, Progress } from '@ds/core';
import { DataTable } from '@ds/data';
import { Alert } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { emRisco } from '@/data/plataformaDaVecchia';
import { dados } from '@/data/porta';
import type { CargaDaPlataforma, ClinicaNaPlataforma } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { quandoRelativo } from './Visao';

export async function carregarMetricas(): Promise<CargaDaPlataforma> {
    return dados().plataforma();
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Assinaturas por mês de criação, nos últimos doze. */
function crescimento(clinicas: ClinicaNaPlataforma[]): { mes: string; rotulo: string; total: number }[] {
    const hoje = new Date();
    return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(hoje.getFullYear(), hoje.getMonth() - (11 - i), 1);
        const mes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return {
            mes,
            rotulo: `${MESES[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`,
            total: clinicas.filter((c) => c.criadoEm.slice(0, 7) === mes).length,
        };
    });
}

export function Metricas() {
    const carga = useLoaderData() as CargaDaPlataforma;
    const navigate = useNavigate();
    const rota = rotaPorId('metricas');

    const ultimo = useLocation().pathname.split('/').pop() ?? '';
    const aba = (rota?.children ?? []).some((c) => c.path === ultimo) ? ultimo : 'crescimento';

    const clinicas = carga.clinicas;
    const canceladas = clinicas.filter((c) => c.situacao === 'cancelada');
    const jamaisEntraram = clinicas.filter((c) => !c.ultimoAcesso);
    const ativaram = clinicas.filter((c) => c.agendamentos30d > 0);
    const serie = crescimento(clinicas);
    const maior = Math.max(...serie.map((s) => s.total), 1);

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader title="Métricas" description="O que a base de clientes conta sobre si — sem receita, que ainda não tem preço.">
                <Tabs
                    ariaLabel="Recortes das métricas" activeId={aba}
                    onChange={(id) => navigate(`/admin/metricas/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({ id: c.path, label: c.label }))}
                />
            </PageHeader>

            {aba === 'crescimento' && (
                <>
                    <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                        <KpiCard label="Clínicas na base" value={String(clinicas.length)} hint="não arquivadas" icon={<Icon name="building-2" size={16} />} />
                        <KpiCard label="Entraram em 12 meses" value={String(serie.reduce((s, m) => s + m.total, 0))} hint="por data de cadastro" icon={<Icon name="trending-up" size={16} />} />
                        <KpiCard label="Em trial" value={String(clinicas.filter((c) => c.situacao === 'trial').length)} hint="ainda não pagam" icon={<Icon name="flask-conical" size={16} />} />
                        <KpiCard label="Canceladas" value={String(canceladas.length)} hint="saíram da base" deltaTone={canceladas.length ? 'down' : 'up'} icon={<Icon name="user-x" size={16} />} />
                    </div>

                    <Card title="Novas clínicas por mês" subtitle="Últimos doze meses, por data de cadastro" padding={18}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160 }}>
                            {serie.map((m) => (
                                <span key={m.mes} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
                                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{m.total || ''}</span>
                                    <span
                                        style={{
                                            width: '100%', maxWidth: 30,
                                            height: `${Math.max(m.total === 0 ? 2 : 6, Math.round((m.total / maior) * 100))}%`,
                                            borderRadius: '4px 4px 0 0',
                                            background: m.total ? 'var(--burgundy-700)' : 'var(--burgundy-100)',
                                        }}
                                    />
                                    <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{m.rotulo}</span>
                                </span>
                            ))}
                        </div>
                    </Card>
                </>
            )}

            {aba === 'churn' && (
                <>
                    <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                        <KpiCard
                            label="Cancelamento acumulado"
                            value={`${clinicas.length ? Math.round((canceladas.length / clinicas.length) * 100) : 0}%`}
                            hint={`${canceladas.length} de ${clinicas.length} clínicas`}
                            deltaTone={canceladas.length ? 'down' : 'up'}
                            icon={<Icon name="user-x" size={16} />}
                        />
                        <KpiCard label="Em risco" value={String(clinicas.filter((c) => emRisco(c)).length)} hint="sem acesso há mais de 14 dias" icon={<Icon name="triangle-alert" size={16} />} />
                        <KpiCard label="Inadimplentes" value={String(clinicas.filter((c) => c.situacao === 'inadimplente').length)} hint="assinatura em atraso" icon={<Icon name="circle-alert" size={16} />} />
                    </div>

                    {/* Churn mensal exige a data em que cada uma cancelou; hoje só
                        existe a situação atual. Dizer isso é melhor que desenhar
                        uma curva a partir de um dado que não foi registrado. */}
                    <Alert tone="info" dismissible={false} title="Churn mensal precisa da data do cancelamento">
                        A base guarda a situação de hoje, não quando cada clínica saiu. O acumulado acima é
                        calculável; a curva mês a mês só depois que o cancelamento passar a ser registrado com
                        data — e é a mesma linha que a assinatura já reserva.
                    </Alert>
                </>
            )}

            {aba === 'ativacao' && (
                <>
                    <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                        <KpiCard
                            label="Usaram nos últimos 30 dias" value={String(ativaram.length)}
                            hint={`de ${clinicas.length} clínicas`} icon={<Icon name="activity" size={16} />}
                        />
                        <KpiCard
                            label="Nunca entraram" value={String(jamaisEntraram.length)}
                            hint="assinaram e não usaram" deltaTone={jamaisEntraram.length ? 'down' : 'up'}
                            icon={<Icon name="door-closed" size={16} />}
                        />
                    </div>

                    <Card title="Uso por clínica" subtitle="Agendamentos nos últimos 30 dias" padding={18}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {clinicas.map((c) => (
                                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                                    <span style={{ width: 170 }}>{c.nome}</span>
                                    <Progress
                                        value={Math.min(100, Math.round((c.agendamentos30d / Math.max(1, Math.max(...clinicas.map((x) => x.agendamentos30d)))) * 100))}
                                        tone={c.agendamentos30d ? 'brand' : 'warning'}
                                        style={{ flex: 1 }}
                                    />
                                    <span style={{ width: 130, textAlign: 'right', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                        {c.agendamentos30d} · {quandoRelativo(c.ultimoAcesso)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </>
            )}

            {aba === 'ltv' && (
                <>
                    <Alert tone="warning" dismissible={false} title="LTV depende de preço, e nenhum plano tem">
                        Valor de vida do cliente é receita por clínica multiplicada pelo tempo que ela fica. O
                        tempo já dá para medir; a receita não, enquanto o preço do plano não for definido em
                        Planos.
                    </Alert>

                    <Card title="Tempo de casa" subtitle="O lado do LTV que já é calculável" padding={0}>
                        <DataTable
                            columns={[
                                { key: 'nome', header: 'Clínica' },
                                { key: 'plano', header: 'Plano', width: 150 },
                                { key: 'situacao', header: 'Situação', width: 140 },
                                {
                                    key: 'criadoEm', header: 'Cliente há', align: 'right', width: 150,
                                    render: (c) => quandoRelativo(c.criadoEm),
                                },
                            ]}
                            rows={clinicas} rowKey="id"
                            stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        />
                    </Card>
                </>
            )}
        </div>
    );
}
